var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx } from "react/jsx-runtime";
import { Stage } from '@pixi/layers';
import { produce } from 'immer';
import { Simple as Cull } from 'pixi-cull';
import { Viewport } from 'pixi-viewport';
import { Application, settings as PixiSettings, Ticker } from 'pixi.js';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, } from 'react';
import { useDeepCompareEffect, useUnmount, useUpdateEffect } from 'react-use';
import { FlowNodeTypes } from 'components/powerFlowShared/components/types';
import { maxZoom, minZoom } from 'flow-builder/constants';
import { useActionHandlers } from 'flow-builder/hooks/action/useHandlers/useHandlers';
import { useNodeHandlers } from 'flow-builder/hooks/node/useHandlers/useHandlers';
import { useUIHandlers } from 'flow-builder/hooks/ui/useHandlers/useHandlers';
import { Node } from 'flow-builder/renderers/node/nodeComponent';
import { HighlightType } from 'flow-builder/types/node';
import events, { CANVAS_CLICK, CANVAS_ZOOM, } from 'flow-builder/utils/canvas/events/events';
import { getStartBounds } from 'flow-builder/utils/canvas/getStartBounds/getStartBounds';
import { getConnections } from 'flow-builder/utils/node/getConnections/getConnections';
import { getRootTree } from 'flow-builder/utils/node/getNodeTree/getNodeTree';
import { getRelations } from 'flow-builder/utils/node/getRelations/getRelations';
import { getScopeVars } from 'flow-builder/utils/scopeVars/getScopeVars/getScopeVars';
import { HighlightMatch, useHighlight } from './hooks/useHighlight';
const EditorContext = createContext(null);
export const EditorContextProvider = ({ nodeAttributes, extraData, flowType, flow: { actions }, invalidNodes, children, }) => {
    var _a, _b;
    const [isCanvasMounted, setIsCanvasMounted] = useState(false);
    const getEditorProps = () => ({
        flow: {
            type: flowType,
            attributes: nodeAttributes,
            data: extraData,
        },
    });
    const [props, setProps] = useState(getEditorProps);
    const [nodeState, setNodeState] = useState(() => {
        const relations = getRelations(nodeAttributes, extraData, actions);
        const connections = getConnections(relations);
        const scopeVars = getScopeVars(nodeAttributes, relations);
        const rootTree = getRootTree(relations);
        return {
            changes: [],
            relations,
            connections,
            scopeVars,
            rootTree,
        };
    });
    const [attachState, setAttachState] = useState({
        actionNodeId: null,
        hoveredNodeId: null,
        isDragging: false,
    });
    const [highlightState, setHighlightState] = useHighlight({
        relations: nodeState.relations,
        invalidNodes,
    });
    const [selectState, setSelectState] = useState({ node: null });
    const [viewState, setViewState] = useState({ scale: 1 });
    useUpdateEffect(() => {
        events.publish(CANVAS_ZOOM, { zoom: viewState.scale });
    }, [viewState.scale]);
    useDeepCompareEffect(() => {
        setProps(getEditorProps);
    }, [flowType, extraData, nodeAttributes]);
    const state = useMemo(() => ({
        nodeState,
        setNodeState,
        attachState,
        setAttachState,
        highlightState,
        setHighlightState,
        selectState,
        setSelectState,
        viewState,
        setViewState,
    }), [
        // Node state
        nodeState,
        setNodeState,
        // Attach state
        attachState,
        setAttachState,
        // Highlight state
        highlightState,
        setHighlightState,
        // Select state
        selectState,
        setSelectState,
        // View state
        viewState,
        setViewState,
    ]);
    const _container = useRef(null);
    const app = useRef(null);
    const viewport = useRef(null);
    const cull = useRef(null);
    const connections = useRef({});
    const nodes = useRef({});
    const view = useMemo(() => {
        if (!isCanvasMounted ||
            !app.current ||
            !viewport.current ||
            !cull.current) {
            return {
                isReady: false,
                app: null,
                viewport: null,
                cull: null,
                connections,
                nodes,
            };
        }
        return {
            isReady: true,
            app: app.current,
            viewport: viewport.current,
            cull: cull.current,
            connections,
            nodes,
        };
    }, [isCanvasMounted, app, viewport, cull, connections, nodes]);
    const deletableSelection = useMemo(() => {
        if (highlightState.type !== HighlightType.INFO) {
            return { nodes: [], disabled: true };
        }
        return Object.entries(highlightState.nodes).reduce((acc, [id, { match }]) => {
            // It's possible the highlighted `id` is not a node, but a connection ending with a tag.
            if (!nodeState.relations[id]) {
                return acc;
            }
            const isActionNode = nodeState.relations[id].nodeType === FlowNodeTypes.ACTION;
            const isRemovable = nodeState.relations[id].isRemovable;
            const isMatchBoth = match === HighlightMatch.Both;
            const isMatchNode = match === HighlightMatch.Node;
            if (!isActionNode && isRemovable && (isMatchBoth || isMatchNode)) {
                acc.nodes.push(id);
            }
            const { parentId } = nodeState.relations[id];
            // If parent is not in `nodes` it's the "root" node
            if (!parentId || acc.nodes.indexOf(parentId) === -1) {
                const component = nodes.current[id];
                if (component instanceof Node) {
                    const { descendants, grants } = nodeState.scopeVars[id] || {};
                    acc.disabled =
                        // Check if the node arrow component is in a locked state.
                        // This is easier & faster than re-calculating this ourselves again.
                        component.nodeArrow.isLocked ||
                            // Check whether any of the descendants consume any of the grants.
                            !!(descendants === null || descendants === void 0 ? void 0 : descendants.some((descendantId) => {
                                var _a;
                                return (_a = nodeState.scopeVars[descendantId]) === null || _a === void 0 ? void 0 : _a.needs.some((name) => grants.includes(name));
                            }));
                }
            }
            return acc;
        }, { nodes: [], disabled: false });
    }, [nodeState, highlightState]);
    const derived = useMemo(() => ({
        deletableSelection,
    }), [deletableSelection]);
    const nodeHandlers = useNodeHandlers({ props, state, derived, view });
    const actionHandlers = useActionHandlers({ props, state });
    const { handlers: uiHandlers, elements } = useUIHandlers({
        props,
        state,
        view,
        handlers: { node: nodeHandlers },
    });
    // Handle `extraData` & `nodeAttributes` change.
    // Change in them can affect a change in `translatePayload` output (in e.g business hours node).
    useEffect(() => {
        setNodeState(produce((state) => {
            const { relations } = state;
            const newRelations = getRelations(nodeAttributes, extraData, Object.values(relations));
            const newConnections = getConnections(newRelations);
            state.relations = newRelations;
            state.connections = newConnections;
        }));
    }, [extraData, nodeAttributes]);
    // Adjust viewport size when screen size changes.
    useEffect(() => {
        var _a, _b;
        if (!viewport.current || !_container.current) {
            return;
        }
        viewport.current.screenWidth = (_a = _container.current) === null || _a === void 0 ? void 0 : _a.offsetWidth;
        viewport.current.screenHeight = (_b = _container.current) === null || _b === void 0 ? void 0 : _b.offsetHeight;
    }, [
        viewport,
        (_a = _container.current) === null || _a === void 0 ? void 0 : _a.offsetWidth,
        (_b = _container.current) === null || _b === void 0 ? void 0 : _b.offsetHeight,
    ]);
    // Destroy app on unmount
    useUnmount(() => {
        if (!view.isReady) {
            return;
        }
        viewport.current = null;
        cull.current = null;
        app.current = null;
        setIsCanvasMounted(false);
    });
    const onAppInit = useCallback((container) => {
        if (view.isReady || !container) {
            return;
        }
        PixiSettings.RESOLUTION = 2;
        const { offsetWidth: width, offsetHeight: height } = container;
        _container.current = container;
        app.current = new Application({
            powerPreference: 'high-performance',
            resizeTo: container,
            backgroundColor: 0xf5f7fa,
            autoDensity: true,
        });
        viewport.current = new Viewport({
            screenWidth: width,
            screenHeight: height,
            worldWidth: width,
            worldHeight: height,
            interaction: app.current.renderer.plugins.interaction,
        });
        cull.current = new Cull();
        const { input } = viewport.current;
        // Fit all the nodes in viewport on initial load.
        const startBounds = getStartBounds(actions);
        const boundWidth = startBounds.end[0] - startBounds.start[0];
        const boundHeight = startBounds.end[1] - startBounds.start[1];
        const boundScaleX = width / boundWidth / 1.5;
        const boundScaleY = height / boundHeight / 1.5;
        const scale = Math.min(boundScaleX, boundScaleY, 1);
        const boundMidPointX = (startBounds.start[0] + boundWidth / 2) * scale * -1;
        const boundMidPointY = (startBounds.start[1] + boundHeight / 2) * scale * -1;
        const boundMidPointAdjustedScreenX = boundMidPointX + width / 2;
        const boundMidPointAdjustedScreenY = boundMidPointY + height / 2;
        setViewState({ scale });
        viewport.current.setZoom(scale);
        viewport.current.position.set(boundMidPointAdjustedScreenX, boundMidPointAdjustedScreenY);
        // This is a fix for panning stopping when mouse goes outside the canvas display object
        viewport.current.removeListener('pointerout', input.up);
        /*
         * Configure viewport
         * @see https://davidfig.github.io/pixi-viewport/jsdoc/
         */
        viewport.current
            // Allow panning
            .drag()
            // Allow pinch to zoom
            .pinch()
            // Allow mouse wheel to zoom
            .wheel()
            // Update `scale` state.
            // This is for ZoomControls component to be able to display zoom information.
            .on('zoomed', (event) => {
            const viewport = event.viewport;
            const scalePoint = viewport.scale;
            if (scalePoint.x < minZoom || scalePoint.x > maxZoom) {
                // Configure min / max zoom
                viewport.clampZoom({
                    maxScale: maxZoom,
                    minScale: minZoom,
                });
            }
            setViewState({ scale: scalePoint.x });
        })
            .on('click', (event) => {
            events.publish(CANVAS_CLICK, event);
        });
        /*
         * Layers won't work if we don't set root as `Stage`
         * @see https://api.pixijs.io/@pixi/layers/Stage.html
         */
        app.current.stage = new Stage();
        app.current.stage.addChild(viewport.current);
        container.appendChild(app.current.view);
        container.addEventListener('wheel', (e) => e.preventDefault());
        Ticker.shared.add(() => {
            if (viewport.current && cull.current) {
                cull.current.cull(viewport.current.getVisibleBounds());
                viewport.current.dirty = false;
            }
        });
        const isPixiInspectorOnWindow = Reflect.has(window, '__PIXI_INSPECTOR_GLOBAL_HOOK__');
        if (isPixiInspectorOnWindow) {
            (() => __awaiter(void 0, void 0, void 0, function* () {
                const PIXI = yield import(/* webpackChunkName: "pixijs" */ 'pixi.js');
                const inspector = Reflect.get(window, '__PIXI_INSPECTOR_GLOBAL_HOOK__');
                inspector.register({ PIXI });
            }))();
        }
        setIsCanvasMounted(true);
    }, [view.isReady, actions]);
    const canvasHandlers = useMemo(() => ({
        onAppInit,
    }), [onAppInit]);
    const context = useMemo(() => ({
        props,
        state,
        derived,
        view,
        elements,
        handlers: {
            node: nodeHandlers,
            action: actionHandlers,
            ui: uiHandlers,
            canvas: canvasHandlers,
        },
    }), [
        props,
        state,
        view,
        derived,
        elements,
        nodeHandlers,
        actionHandlers,
        uiHandlers,
        canvasHandlers,
    ]);
    return (_jsx(EditorContext.Provider, Object.assign({ value: context }, { children: children })));
};
export const useEditorContext = () => {
    const context = useContext(EditorContext);
    if (!context) {
        throw new Error('Missing EditorContext');
    }
    return context;
};
